import { api } from '../client';

export type StockStatus = 'Optimal' | 'Low' | 'Stockout' | 'Overstock';

export interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  unit: string;
  companyId: string;
  company?: { id: string; name: string; displayName: string };
  minStockLevel: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  unitCost?: number;
  isActive: boolean;
  inventoryLevels?: InventoryLevel[];
  aggregated?: {
    totalQuantity: number;
    totalReserved: number;
    totalAvailable: number;
    locationCount: number;
    totalValue?: number;
  };
  status?: StockStatus;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryLevel {
  id: string;
  itemId: string;
  locationId: string;
  location?: { id: string; name: string; code: string; type: string };
  quantity: number;
  reservedQty: number;
  availableQty: number;
  lastCountDate?: string;
  status?: StockStatus;
}

export interface CreateInventoryItemDto {
  sku: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  unit?: string;
  companyId: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  unitCost?: number;
  isActive?: boolean;
}

export interface QueryInventoryParams {
  search?: string;
  category?: string;
  locationId?: string;
  status?: StockStatus;
  companyId?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface RestockDto {
  itemId: string;
  sourceLocationId: string;
  destinationLocationId: string;
  quantity: number;
  unitPrice?: number;
  notes?: string;
}

export interface TransferDto {
  sourceLocationId: string;
  destinationLocationId: string;
  items: { itemId: string; quantity: number }[];
  notes?: string;
}

export interface InventoryDashboard {
  metrics: {
    totalItems: number;
    totalLocations: number;
    totalValue: number;
    activeAlerts: number;
    inTransitShipments: number;
  };
  stockDistribution: {
    optimal: number;
    low: number;
    stockout: number;
    overstock: number;
  };
}

export const inventoryApi = {
  async getAll(params?: QueryInventoryParams) {
    const response = await api.get<{
      data: InventoryItem[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/api/inventory', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<InventoryItem>(`/api/inventory/${id}`);
    return response.data;
  },

  async getDashboard(companyId?: string) {
    const response = await api.get<InventoryDashboard>('/api/inventory/dashboard', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getLevels(id: string) {
    const response = await api.get<InventoryLevel[]>(`/api/inventory/${id}/levels`);
    return response.data;
  },

  async addLevel(id: string, data: { locationId: string; quantity?: number }) {
    const response = await api.post(`/api/inventory/${id}/levels`, data);
    return response.data;
  },

  async updateLevel(id: string, locationId: string, data: { quantity: number; reservedQty?: number }) {
    const response = await api.patch(`/api/inventory/${id}/levels/${locationId}`, data);
    return response.data;
  },

  async restock(data: RestockDto) {
    const response = await api.post('/api/inventory/restock', data);
    return response.data;
  },

  async transfer(data: TransferDto) {
    const response = await api.post('/api/inventory/transfer', data);
    return response.data;
  },

  async create(data: CreateInventoryItemDto) {
    const response = await api.post<InventoryItem>('/api/inventory', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateInventoryItemDto>) {
    const response = await api.patch<InventoryItem>(`/api/inventory/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<InventoryItem>(`/api/inventory/${id}`);
    return response.data;
  },
};
